/* Native Shopify contact submission only. No fetch, storage or success spoofing. */
(() => {
  'use strict';
  if (window.NebuliteStockNotify) return;
  const BEGIN = 'NEBULITE_STOCK_NOTIFY_V1_BEGIN';
  const END = 'NEBULITE_STOCK_NOTIFY_V1_END';
  const SHOP = 'nebulite-collection.myshopify.com';
  const VERSION = 'stock-once-2026-10-08';
  const CONSENT = Object.freeze({
    de: 'Ich möchte einmalig per E-Mail benachrichtigt werden, wenn genau diese Variante wieder auf Lager und bestellbar ist. Keine Newsletter-Anmeldung.',
    en: 'Email me once when this exact variant is back in stock and available to order. This does not subscribe me to a newsletter.'
  });
  const EMAIL_ERROR = Object.freeze({de: 'Bitte prüfe die E-Mail-Adresse.', en: 'Please check the email address.'});
  const EMAIL = /^[A-Za-z0-9.!#$%&'*+\/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;
  const states = new Map(); // Page memory only; never persist customer input.
  const instances = new WeakMap();
  const selector = '[data-stock-notify]';
  const id = value => {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    const text = String(value ?? '');
    return /^[1-9][0-9]{0,15}$/.test(text) ? text : null;
  };
  // Match the complete raw native handle; never trim, decode or slugify it.
  function validHandle(value) {
    return typeof value === 'string' && /^[a-z0-9][a-z0-9®-]*$/u.exec(value)?.[0] === value;
  }
  function sameProductPath(value, handle, prefix) {
    if (!validHandle(handle) || typeof value !== 'string') return false;
    return value === prefix + handle || value.replace(/%[0-9a-f]{2}/gi, x => x.toUpperCase()) === prefix + encodeURIComponent(handle);
  }
  function readModel(root) {
    const m = JSON.parse(root.querySelector('[data-stock-model]').textContent);
    if (m.shop !== SHOP || m.consent_version !== VERSION || m.consent_text !== CONSENT[m.locale]
        || !id(m.product_id) || !validHandle(m.handle)
        || typeof m.title !== 'string' || typeof m.product_url !== 'string'
        || !Array.isArray(m.variants) || !m.variants.length || m.variants.length > 1000) throw new Error('model');
    const variants = new Map();
    for (const v of m.variants) {
      const key = id(v.id);
      if (!key || variants.has(key) || typeof v.available !== 'boolean' || typeof v.title !== 'string') throw new Error('variant');
      variants.set(key, {...v, id: key});
    }
    const paths = ['/products/', m.locale === 'de' ? '/de/products/' : '/en/products/'];
    if (!paths.some(prefix => sameProductPath(m.product_url, m.handle, prefix)
        || sameProductPath(m.product_url, m.handle, window.location.origin + prefix))
        || !paths.some(prefix => sameProductPath(window.location.pathname, m.handle, prefix))) throw new Error('product_url');
    const url = new URL(m.product_url, window.location.origin);
    if (url.origin !== 'https://nebulite.berlin' || url.origin !== window.location.origin ||
        url.username || url.password || url.search || url.hash) throw new Error('product_url');
    return {...m, product_id: id(m.product_id), selected_variant_id: id(m.selected_variant_id), variants, url};
  }
  function productForm(formId) {
    const form = document.getElementById(formId);
    return form?.tagName === 'FORM' ? form : null;
  }
  function selectedId(form) { return id(form?.elements.namedItem('id')?.value); }
  function remember(instance) {
    instance.state.email = instance.email.value;
    instance.state.consent = instance.consent.checked;
  }
  function render(i, variantId, nativeVariant) {
    const address = i.email.value;
    i.email.setCustomValidity(address && (!EMAIL.test(address) || address.length > 254
      || address !== address.trim() || address.includes('..') || /\.comj$/i.test(address))
      ? EMAIL_ERROR[i.model.locale] : '');
    let v = i.model.variants.get(id(variantId));
    if (nativeVariant && (!v || id(nativeVariant.id) !== v.id || typeof nativeVariant.available !== 'boolean')) v = null;
    // Availability from the current native event wins over the original HTML snapshot.
    if (v && nativeVariant) v = {...v, available: nativeVariant.available};
    i.variant = v || null;
    i.message.value = ''; // Never leave a stale request ID in the submitted field.
    i.returnTo.value = ''; // Clear the old redirect at the same binding barrier.
    const confirmedBinding = !!v && selectedId(productForm(i.formId)) === v.id;
    const soldOut = confirmedBinding && v.available === false;
    const nativeSuccess = i.success && i.submittedVariant === v?.id;
    if (i.success) i.success.hidden = !nativeSuccess;
    const hasErrors = !!i.root.querySelector('[data-stock-native-errors]');
    i.root.hidden = !soldOut && !nativeSuccess && !hasErrors;
    i.heading.hidden = !soldOut;
    i.selection.hidden = !soldOut;
    i.fields.hidden = !soldOut || nativeSuccess;
    i.fields.disabled = !soldOut || nativeSuccess;
    i.button.disabled = !soldOut || nativeSuccess;
    if (!v || !confirmedBinding) {
      i.error.hidden = !hasErrors;
      return;
    }
    i.error.hidden = true;
    const url = new URL(i.model.url);
    if (['/products/', i.model.locale === 'de' ? '/de/products/' : '/en/products/'].some(prefix =>
        sameProductPath(window.location.pathname, i.model.handle, prefix))) url.pathname = window.location.pathname;
    url.searchParams.set('variant', v.id);
    // Shopify's native accepted POST redirects here. Keep its contact success
    // flag on that destination; only Liquid's server-rendered success block
    // controls the UI. The requested product link remains canonical below.
    const returnUrl = new URL(url);
    returnUrl.searchParams.set('contact_posted', 'true');
    i.returnTo.value = soldOut ? returnUrl.pathname + returnUrl.search : '';
    i.link.href = url.toString();
    i.link.textContent = v.title === 'Default Title' ? i.model.title : `${i.model.title} — ${v.title}`;
    if (soldOut) {
      const request = {shop: SHOP, product_id: i.model.product_id, variant_id: v.id,
        handle: i.model.handle, locale: i.model.locale, email: i.email.value,
        consent_version: VERSION, consent_text: i.model.consent_text};
      i.message.value = `${BEGIN}\n${JSON.stringify(request)}\n${END}`;
    }
  }
  function init(root) {
    if (instances.has(root)) return;
    let model;
    try { model = readModel(root); } catch { root.hidden = true; return; }
    const formId = root.getAttribute('data-stock-observe-form');
    const form = root.querySelector('form');
    if (!formId || !form || form.closest('form') !== form || root.closest('form')) { root.hidden = true; return; }
    const returns = Array.from(form.querySelectorAll('input[name="return_to"]'));
    if (returns.length !== 1 || returns[0].type !== 'hidden') { root.hidden = true; return; }
    const key = `${formId}:${model.product_id}`;
    const email = root.querySelector('[data-stock-email]');
    const consent = root.querySelector('[data-stock-consent]');
    const state = states.get(key) || {email: email.value, consent: consent.checked};
    states.set(key, state);
    email.value = state.email; consent.checked = state.consent;
    const i = {root, model, formId, form, key, state, email, consent, returnTo:returns[0],
      message: root.querySelector('[data-stock-message]'), fields: root.querySelector('[data-stock-fields]'),
      button: root.querySelector('[data-stock-submit]'), heading: root.querySelector('[data-stock-heading]'),
      selection: root.querySelector('[data-stock-selection]'), link: root.querySelector('[data-stock-product-link]'),
      error: root.querySelector('[data-stock-binding-error]'), success: root.querySelector('[data-stock-native-success]'),
      submittedVariant: model.selected_variant_id};
    instances.set(root, i);
    form.addEventListener('input', () => { remember(i); render(i, i.variant?.id, i.variant); });
    form.addEventListener('change', () => { remember(i); render(i, i.variant?.id, i.variant); });
    form.addEventListener('submit', event => {
      remember(i);
      render(i, i.variant?.id, i.variant);
      if (i.fields.disabled || !i.message.value || !i.returnTo.value || !form.checkValidity()) {
        event.preventDefault();
        i.error.hidden = !i.fields.disabled;
        form.reportValidity();
      }
      // Valid submission stays native, including Shopify CAPTCHA and redirect.
      // Never call submit(), requestSubmit(), fetch() or replace the form action.
    });
    const current = selectedId(productForm(formId));
    render(i, current === model.selected_variant_id ? current : null);
  }
  function roots(formId) {
    return Array.from(document.querySelectorAll(selector)).filter(root => root.getAttribute('data-stock-observe-form') === formId);
  }
  function eventFormId(event) {
    const target = event.target;
    if (target?.tagName !== 'FORM') return null;
    const formId = target.getAttribute('id');
    const detailId = event.detail?.formId;
    // The deployed theme uses form.id, which may be shadowed by its named id input.
    if (detailId !== undefined && detailId !== formId && detailId !== target.elements.namedItem('id')) return null;
    return formId;
  }
  document.addEventListener('product:rerender', event => {
    const formId = eventFormId(event);
    if (!formId) return;
    for (const root of roots(formId)) {
      init(root);
      const i = instances.get(root);
      if (!i) continue;
      remember(i);
      // This non-bubbling event occurs before the native variant:change.
      // Clear the old binding synchronously; MutationObserver restores new nodes.
      render(i, null);
    }
  }, true);
  document.addEventListener('variant:change', event => {
    const formId = eventFormId(event);
    if (!formId) return;
    const v = event.detail?.variant;
    for (const root of roots(formId)) {
      init(root);
      const i = instances.get(root);
      if (i) render(i, v?.id, v);
    }
  }, true);
  function scan(node) {
    if (node.nodeType !== 1 && node.nodeType !== 9) return;
    if (node.matches?.(selector)) init(node);
    node.querySelectorAll?.(selector).forEach(init);
  }
  function start() {
    scan(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) scan(node);
    }).observe(document.body, {childList: true, subtree: true});
  }
  window.NebuliteStockNotify = Object.freeze({version: VERSION});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
